/*
 * @Author: Sergiy Samborskiy 
 * @Date: 2018-01-29 19:32:11 
 * @Last Modified by: Sergiy Samborskiy
 * @Last Modified time: 2019-03-14 17:11:11
 */

import * as React from "react";
import * as Fuse from "fuse.js";
import cx from "classnames";
import _ from "lodash";

import TextField from "material-ui/TextField";
import MenuItem from "material-ui/MenuItem";
import RaisedButton from "material-ui/RaisedButton";
import IconMenu from 'material-ui/IconMenu';
import IconButton from 'material-ui/IconButton';
import Popover from 'material-ui/Popover';
import Paper from 'material-ui/Paper';

import { AutoSizer } from "react-virtualized/dist/es/AutoSizer";
import { List } from "react-virtualized/dist/es/List";
import { ScrollSync } from "react-virtualized/dist/es/ScrollSync";

import EditIcon from "material-ui/svg-icons/image/edit";
import DeleteIcon from "material-ui/svg-icons/action/delete";
import FileDownload from 'material-ui/svg-icons/file/file-download';
import DeleteSweep from 'material-ui/svg-icons/content/delete-sweep';
import MoreVertIcon from 'material-ui/svg-icons/navigation/more-vert';

import AwaitableBtn from "components/Base/AwaitableBtn";

import { 
    loadDefFile, 
    loadLstFile, 
    buildLst, 
    getEmptyFeatures, 
    getColumns, 
    saveLst, 
    saveDef, 
    validator, 
    removeDuplicates, 
    calculateDuplicateEntries 
} from "./service";

import * as gazetteerService from "services/gazetters";

import helpers from "helpers/index";
import { execute, UserPerms } from "store/configureStore";

import s from "./styles.scss";

class DynamicKey {
    KEY = Symbol("dynamic key");

    /**
     * @private
     */
    value = 1;

    get next() { return this.value++; }
}

// TODO: implement and integrate as source of filterable/sortable data with fast access by key
class FastDynStore {
    contructor(source) {
        this.dKey = new DynamicKey();
        this.source = source;
        this.view = [...source];
        this.index = this._prepareData(source)
    }

    _prepareData(items) {
        const map = new Map();

        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (!item[this.dKey.KEY]) {
                item[this.dKey.KEY] = this.dKey.next;
            }

            map.set(item[this.dKey.KEY], item);
        }
        return map;
    }
}

export default class GazetteerEditorPage extends React.Component {

    state = {
        servers: [
            "cargo",
            "vessel",
            "common"
        ],

        defData: [],
        defView: [],
        defVersion: undefined,

        selectedLst: undefined
    }

    defList;
    lstList;

    constructor(props, context) {
        super(props, context);

        this.dKey = new DynamicKey();

        this.onDefCellChanged = this.onDefCellChanged.bind(this);
        this.onLstCellChanged = this.onLstCellChanged.bind(this);
        this.onLoadLst = this.onLoadLst.bind(this);
        this.onDedupLst = this.onDedupLst.bind(this);
        this.onDeleteLstEntry = this.onDeleteLstEntry.bind(this);
        this.onDeleteDefEntry = this.onDeleteDefEntry.bind(this);
        this.renderDefRow = this.renderDefRow.bind(this);
        this.renderLstFeaturesRow = this.renderLstFeaturesRow.bind(this);
        this.onFilterDef = _.debounce(this.onFilterDef.bind(this), 150, { leading: true });
        this.onFilterLst = _.debounce(this.onFilterLst.bind(this), 150, { leading: true });

        this.onAddFeature = this.onAddFeature.bind(this);
        this.onAddLstEntry = this.onAddLstEntry.bind(this);
        this.onDownloadLst = this.onDownloadLst.bind(this);
        this.onSaveLst = this.onSaveLst.bind(this);

        this.onSaveDef = this.onSaveDef.bind(this);
        this.onAddDefRow = this.onAddDefRow.bind(this);

        this.onLstFeatureChanged = this.onLstFeatureChanged.bind(this);

        this.changes = new Map();
        this.invalidDefRows = new Set();
        this.invalidRows = new Set();
        this.isNewFeatureNameValid = true;

        window.editor = this;
    }

    async componentDidMount() {
        const [version, data] = await execute(loadDefFile);

        this.setState({
            defIndex: this.prepareData(data),
            defData: data,
            defView: [...data],
            defVersion: version
        });
    }

    prepareData(items) {
        const map = new Map();

        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (!item[this.dKey.KEY]) {
                item[this.dKey.KEY] = this.dKey.next;
            }

            map.set(item[this.dKey.KEY], item);
        }
        return map;
    }

    onDownloadLst() {
        const content = buildLst(this.state.lstData, this.columns);

        const url = window.URL.createObjectURL(new window.Blob([content], { type: 'text/plain' }));
        const element = document.createElement('a');
        element.setAttribute('href', url);
        element.setAttribute('download', this.state.selectedLst);
      
        element.click();

        // should be safe to revoke here as specs stated "Requests that were started before the url was revoked should still succeed." 
        window.URL.revokeObjectURL(url);
    }

    async onSaveLst() {
        if (this.invalidRows.size > 0) {
            return alert(`${this.state.selectedLst} has ${this.invalidRows.size} error(s)! Fix them before saving lst.`);
        }

        const emptyFeatures = getEmptyFeatures(this.state.lstData, this.columns);
        if (emptyFeatures.length > 0) {
            return alert(`Gazetteer ${this.state.selectedLst} has empty features: ${emptyFeatures.join(", ")}. \nRemove them or fill with at least one value per feature.`);
        }

        const content = buildLst(this.state.lstData, this.columns);
        const newVersion = await execute(saveLst, this.state.selectedLst, this.state.selectedLstVersion, content);

        this.changes.clear();

        const duplicatesCount = calculateDuplicateEntries(this.state.lstData);

        this.setState({
            selectedLstVersion: newVersion,
            lstDuplicatesCount: duplicatesCount
        });
    }

    async onSaveDef() {
        if (this.invalidDefRows.size > 0) {
            return alert(`File has ${this.invalidDefRows.size} error(s)! Fix them before saving def.`);
        }

        const [newVersion, data] = await execute(saveDef, this.state.defVersion, this.state.defData);
        
        this.setState({
            defIndex: this.prepareData(data),
            defData: data,
            defView: [...data],
            defVersion: newVersion
        });
    }

    async onDedupLst() {
        await execute(removeDuplicates, this.state.selectedLst);

        const defEntry = this.state.defIndex.get(this.state.selectedDefKey);

        if (defEntry) {
            await this.loadLst(defEntry);
        }
    }

    onDefCellChanged(e, value) {
        const el = e.target;
        if (value === undefined) {
            value = el.value;
        }
        const field = el.dataset["field"];
        const index = parseInt(el.closest("[data-key]").dataset["key"]);

        if (validator.defValue(value)) {
            el.classList.remove("invalid");
            this.removeInvalidMark(this.invalidDefRows, field, index.toString());
        }
        else {
            el.classList.add("invalid");
            this.addInvalidMark(this.invalidDefRows, field, index.toString());
        }

        const item = this.state.defIndex.get(index);
        if (item) {
            item[field] = value;
        }
    }

    onLstCellChanged(e, value) {
        const el = e.target;
        if (value === undefined) {
            value = el.value;
        }
        const field = el.dataset["field"];
        const key = parseInt(el.closest("[data-key]").dataset["key"]);


        const validatorFn = field === "$value" ? validator.lstValue : validator.lstFeature;
        if (validatorFn(value)) {
            el.classList.remove("invalid");
            this.removeInvalidMark(this.invalidRows, field, key.toString());
        }
        else {
            el.classList.add("invalid");
            this.addInvalidMark(this.invalidRows, field, key.toString());
        }

        const item = this.state.lstIndex.get(key);
        if (item) {
            item[field] = value;

            let change = this.changes.get(key);
            if (!change) {
                change = {};
                this.changes.set(key, change);
            }
            change[field] = value;
        }
    }

    async onLoadLst(e) {
        const el = e.target;
        const key = parseInt(el.closest("[data-key]").dataset["key"]);

        const defEntry = this.state.defIndex.get(key);

        if (defEntry) {
            await this.loadLst(defEntry);
        }
    }

    async loadLst(defEntry) {
        this.invalidRows.clear();
        
        let version = undefined;
        let items = [{ [this.dKey.KEY]: this.dKey.next }];
        try {
            [version, items] = await execute(loadLstFile, defEntry.fileName);
        }
        catch (err) {
            // .lst file not found on server. we assume it will be created after editing empty one
        }

        const duplicatesCount = calculateDuplicateEntries(items);

        this.columns = getColumns(items);
        this.columns.delete("$value");

        this.lstLocalVersion = 0;
        this.setState({
            lstData: items,
            lstView: [...items],
            lstIndex: this.prepareData(items),

            lstDuplicatesCount: duplicatesCount,
            selectedDefKey: defEntry[this.dKey.KEY],
            selectedLstVersion: version,
            selectedLst: defEntry.fileName
        });
    }

    onAddFeature() {
        const textEdit = this.refs.featureName;
        if (this.columns && textEdit) {
            const featureName = textEdit.getValue().trim();

            this.isNewFeatureNameValid = validator.lstFeature(featureName);
            if (!this.isNewFeatureNameValid || featureName === "") {
                this.forceUpdate();
                return;
            }

            textEdit.input.value = "";
            if (!featureName) {
                return alert("You should write valid feature name first");
            }
            this.columns.add(featureName);
            this.lstLocalVersion++;

            this.forceUpdate();
        }
    }

    onAddLstEntry() {
        const newRow = { [this.dKey.KEY]: this.dKey.next, $value: "" };
        this.state.lstData.push(newRow);
        this.state.lstView = [...this.state.lstData];
        this.state.lstIndex.set(newRow[this.dKey.KEY], newRow);

        this.forceUpdate();

        setTimeout(() => {
            this.lstList.scrollToRow(this.state.lstView.length);
        });

        // do not track as added, because it will be marked as such when modified
        // this.changes.set(index, {});
    }

    onDeleteLstEntry(e) {
        const el = e.target;
        const key = parseInt(el.closest("[data-key]").dataset["key"]);

        if (key > 0) {
            this.changes.set(key, null);
        }
        else {
            this.changes.delete(key);
        }

        this.removeInvalidMark(this.invalidRows, "", key.toString());

        const itemToRemove = this.state.lstIndex.get(key);

        this.state.lstData = this.state.lstData.filter(item => item !== itemToRemove);
        this.state.lstView = [...this.state.lstView.filter(item => item !== itemToRemove)];
        this.state.lstIndex.delete(key);

        this.forceUpdate();
    }

    onAddDefRow() {
        const row = { [this.dKey.KEY]: this.dKey.next, fileName: "", majorType: "", minorType: "", language: "", local: true };

        this.state.defData.push(row);
        this.state.defIndex.set(row[this.dKey.KEY], row);

        this.state.defView.push(row);
        this.forceUpdate();

        setTimeout(() => {
            this.defList.scrollBy(0, Number.MAX_SAFE_INTEGER);
        });
    }

    onDeleteDefEntry(e) {
        const el = e.target;
        const index = parseInt(el.closest("[data-key]").dataset["key"]);

        const itemToDelete = this.state.defIndex.get(index);

        if (!confirm(`Are you sure to remove ${itemToDelete.fileName}`)) {
            return;
        }

        this.state.defData = this.state.defData.filter(item => item !== itemToDelete);
        this.state.defView = [...this.state.defData];
        this.state.defIndex.delete(index);

        this.forceUpdate();
    }

    onLstFeatureChanged(oldValue, newValue, remove = false) {
        if (remove) {
            this.columns.delete(oldValue);

            this.lstLocalVersion++;
            this.removeInvalidMark(this.invalidRows, oldValue);

            return this.forceUpdate();
        }
        if (!newValue || !this.columns.has(oldValue) || this.columns.has(newValue)) {
            return this.forceUpdate();
        }

        this.lstLocalVersion++;
        this.updateInvalidMark(this.invalidRows, oldValue, newValue);

        // convert to array and back to preserve order of renamed entry
        const cols = Array.from(this.columns);
        const index = cols.indexOf(oldValue);
        cols[index] = newValue;
        this.columns = new Set(cols);

        for (const item of this.state.lstData) {
            item[newValue] = item[oldValue];
            item[oldValue] = null;
        }

        this.forceUpdate();
    }

    getValKey(field, key) {
        return `${field}:${key}`;
    }
    /**
     * 
     * @param {Set<string>} invalidRows 
     * @param {string} [field]
     * @param {string} [key] 
     */
    addInvalidMark(invalidRows, field = "", key = "") {
        const value = this.getValKey(field, key);
        invalidRows.add(value);
    }

    /**
     * 
     * @param {Set<string>} invalidRows 
     * @param {string} [field]
     * @param {string} [key] 
     */
    removeInvalidMark(invalidRows, field = "", key = "") {
        const escapedField = helpers.escapeRegExp(field);
        const escapedKey = helpers.escapeRegExp(key);
        const keyReg = `${escapedField}:${escapedKey}`;
        for (const invalidRow of invalidRows) {
            if (invalidRow.match(keyReg)) {
                invalidRows.delete(invalidRow);
            }
        }
    }

    /**
     * 
     * @param {Set<string>} invalidRows 
     * @param {string} oldField
     * @param {string} newField 
     */
    updateInvalidMark(invalidRows, oldField, newField) {
        const escaped = helpers.escapeRegExp(oldField);
        const keyReg = `${escaped}:([^:]+)`;
        for (const invalidRow of invalidRows) {
            const match = invalidRow.match(keyReg);
            if (match) {
                invalidRows.delete(invalidRow);
                invalidRows.add(this.getValKey(newField, match[1]));
            }
        }
    }

    onFilterDef(e, text) {
        if (text.length === 0) {
            this.setState({
                defView: this.state.defData
            });
            return;
        }
        console.time("onFilterDef")
        const fuse = new Fuse(this.state.defData, {
            keys: [
                "fileName",
                "majorType",
                "minorType"
            ],
            shouldSort: false,
            threshold: 0.1,
            distance: 320,
            maxPatternLength: 32,
        });

        const items = fuse.search(text);
        this.setState({
            defView: items
        });
        console.timeEnd("onFilterDef")
    }

    onFilterLst(e, text) {
        if (text.length === 0) {
            this.setState({
                lstView: this.state.lstData
            });
            return;
        }
        console.time("onFilterLst")
        const items = this.find_custom(this.state.lstData,
            [
                "$value",
                ...Array.from(this.columns, col => col),
            ],
            text,
        );
        console.timeEnd("onFilterLst")

        this.setState({
            lstView: items
        });
        this.lstLocalVersion++;
    }

    find_custom(items, fields, text) {
        // search if any field includes string
        const pattern = new RegExp(_.escapeRegExp(text), "i");
        return items.filter(i => {
            return fields.some(f => pattern.test(i[f] || ""));
        });
    }

    renderDefRow(item) {
        const { selectedDefKey } = this.state;
        const key = item[this.dKey.KEY];
        return (
            <tr key={key} data-key={key} className={cx({ loaded: key === selectedDefKey })}>
                <td className="cell readonly">
                    <input disabled={!item.local} defaultValue={item.fileName} data-field="fileName" onChange={this.onDefCellChanged} />
                </td>
                <td className="cell">
                    <input defaultValue={item.majorType} data-field="majorType" onChange={this.onDefCellChanged} />
                </td>
                <td className="cell">
                    <input defaultValue={item.minorType} data-field="minorType" onChange={this.onDefCellChanged} />
                </td>
                <td>
                    <select data-field="$target" defaultValue={item.$target} onChange={this.onDefCellChanged}>
                        <option value="common">common</option>
                        <option value="cargo">cargo</option>
                        <option value="vessel">vessel</option>
                    </select>
                </td>
                <td className="controls">
                    <EditIcon className="edit" onClick={this.onLoadLst} />
                    <DeleteIcon className="delete" onClick={this.onDeleteDefEntry} />
                </td>
            </tr>
        );
    }

    renderDefTable() {
        const disableAdminFunctions = !UserPerms.isAdmin();
        return <React.Fragment>
            <div className='paper-block margin-10'>
                <span className='name-block absolute small-name'>Manage gazetteer index file</span>
                <RaisedButton label="Add row" primary={true} onClick={this.onAddDefRow} />

                <AwaitableBtn>
                    <RaisedButton label="Save" primary={true} onClick={this.onSaveDef} />
                </AwaitableBtn>

                <div className="right">

                    <AwaitableBtn>
                        <RaisedButton
                            label='Sync lst'
                            disabled={disableAdminFunctions}
                            onClick={gazetteerService.saveToStaging}
                            primary
                        />
                    </AwaitableBtn>

                    <AwaitableBtn>
                        <RaisedButton
                            label='Deploy'
                            disabled={disableAdminFunctions}
                            onClick={gazetteerService.saveToProduction}
                            secondary
                        />
                    </AwaitableBtn>
                    
                </div>


            </div>
            <div>
                <TextField placeholder="Search here..." fullWidth onChange={this.onFilterDef} />
            </div>
            <div ref={list => this.defList = list} className="def-list">
                <table>
                    <thead>
                        <tr>
                            <th>File</th>
                            <th>Major type</th>
                            <th>Minor type</th>
                            <th>Type</th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            this.state.defView.map(this.renderDefRow.bind(this))
                        }
                    </tbody>
                </table>
            </div>
        </React.Fragment>;
    }

    renderLstFeaturesRow({ index, style, isScrolling }) {
        const item = this.state.lstView[index];
        const key = item[this.dKey.KEY];

        // FIX: key does not depend on columns state, need some 'version' in case columns moving around
        return (
            <div key={key + this.state.selectedLst + this.state.selectedLstVersion + this.lstLocalVersion} data-key={key} style={{...style, display: "flex", width: "auto" }}>
                {
                    Array.from(this.columns, field => {
                        return <div key={key + field} className="cell">
                            <input style={{ height: style.height }} className={cx({invalid: this.invalidRows.has(this.getValKey(field, key)) })} defaultValue={item[field]} data-field={field} onChange={this.onLstCellChanged} />
                        </div>;
                    })
                }
            </div>
        );
    }

    renderLstTable() {
        if (!this.state.lstView) {
            return <div className="empty">
                <span>Select .lst from left list to edit</span>
            </div>;
        }

        return (
            <React.Fragment>
                <div className='paper-block margin-10'>
                    <span className='name-block absolute small-name'>Manage gazetteer file</span>

                    <div className="with-input">
                        <TextField ref="featureName" defaultValue="" errorText={!this.isNewFeatureNameValid && "invalid feature name" } style={{ height: 35 }} underlineStyle={{ bottom: 0 }} underlineFocusStyle={{ bottom: 0 }} />
                        <RaisedButton label="Add feature" primary={true} onClick={this.onAddFeature} />
                    </div>
                    <RaisedButton label="Add row" primary={true} onClick={this.onAddLstEntry} />
                    <RaisedButton label="Save" primary={true} onClick={this.onSaveLst} />

                    <div className="statuses">
                        { this.state.lstDuplicatesCount > 0
                            ? <div className="dups">{this.state.lstDuplicatesCount} duplicate(s)</div>
                            : null
                        }
                    </div>
                    <IconMenu
                        iconButtonElement={<IconButton><MoreVertIcon /></IconButton>}
                        anchorOrigin={{horizontal: 'left', vertical: 'top'}}
                        targetOrigin={{horizontal: 'left', vertical: 'top'}}
                        className="additional-fns"
                    >
                        <MenuItem primaryText="Download" leftIcon={<FileDownload />} onClick={this.onDownloadLst} />
                        <MenuItem primaryText="Dedup" leftIcon={<DeleteSweep />} onClick={this.onDedupLst} />
                    </IconMenu>
                </div>
                <div>
                    <TextField placeholder="Search here..." fullWidth onChange={this.onFilterLst} onKeyPress={(e, text) => { 
                            if (e.charCode === 13) { this.onFilterLst(e, e.target.value) } 
                        }
                    } />
                </div>
                <ScrollSync>
                    {({
                        onScroll,
                        scrollTop,
                    }) => (
                        <div className="lst-list">
                            <div className="lst-left">
                                <div className="lst-header">
                                    <div className="lst-row fixed-column">
                                        <div className="cell cell-header">
                                            {/* <div>Value</div> */}
                                            Value
                                        </div>
                                    </div>
                                </div>
                                <div className="lst-side-body">
                                    <AutoSizer>
                                        {({ width, height }) => {
                                            return  (
                                                <List
                                                    lstName={this.state.selectedLstVersion}
                                                    lstVersion={this.state.selectedLst}
                                                    lstLocalVersion={this.lstLocalVersion}
                                                    ref={list => this.lstList = list}
                                                    className="fixed-column"
                                                    height={height}
                                                    overscanRowCount={10}
                                                    rowCount={this.state.lstView.length}
                                                    rowHeight={35}
                                                    rowRenderer={({ index, style }) => {
                                                        const item = this.state.lstView[index];
                                                        const key = item[this.dKey.KEY];

                                                        return (
                                                            <div key={key + this.state.selectedLst + this.state.selectedLstVersion} data-key={key} style={style} className="lst-row">
                                                                <div className="controls">
                                                                    <DeleteIcon className="delete" onClick={this.onDeleteLstEntry} />
                                                                </div>
                                                                <div key={key + "$value"} className="cell">
                                                                    <input style={{ height: style.height }} defaultValue={item.$value} data-field="$value" onChange={this.onLstCellChanged} />
                                                                </div>
                                                            </div>
                                                        );
                                                    }}
                                                    width={width}
                                                    onScroll={onScroll}
                                                    scrollTop={scrollTop}
                                                />
                                            )
                                        }}
                                    </AutoSizer>
                                </div>
                            </div>
                            <div className="lst-right">
                                <div className="lst-right-wrapper">
                                    <div className="lst-header">
                                        { //style={{ minWidth: width - lstLeftPanelSize, maxWidth: width - lstLeftPanelSize }}
                                            Array.from(this.columns, (field) => {
                                                return <EditableHeader key={field} defaultValue={field} onChange={this.onLstFeatureChanged} />;
                                            })
                                        }
                                    </div>
                                    <div className="lst-side-body">
                                        <AutoSizer>
                                            {({ width, height }) => (
                                                // `WIDTH: ${width} HEIGHT: ${height}`
                                                <List
                                                    lstName={this.state.selectedLstVersion}
                                                    lstVersion={this.state.selectedLst}
                                                    lstFeatureColCount={this.columns.size}
                                                    lstLocalVersion={this.lstLocalVersion}
                                                    className="grid"
                                                    height={height}
                                                    overscanRowCount={10}
                                                    rowCount={this.state.lstView.length}
                                                    rowHeight={35}
                                                    rowRenderer={this.renderLstFeaturesRow}
                                                    width={width}
                                                    onScroll={onScroll}
                                                    scrollTop={scrollTop}
                                                />
                                            )}
                                        </AutoSizer>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </ScrollSync>
                
            </React.Fragment>
        );
    }
    
    render() {
        return (
            <div className="container-fluid gazetteer-editor">
                <div className="row">
                    <div className="col-xs-6 def-file-entry">
                        <div>Gazetteer Index File</div>
                        {
                            this.renderDefTable()
                        }
                    </div>

                    <div className="col-xs-6 lst-file-entry">
                        <div>Gazetteer: {this.state.selectedLst}</div>
                        {
                            this.renderLstTable()
                        }
                    </div>
                </div>


            </div>
        );
    }
}

function EditableHeader(props) {
    const { onChange, defaultValue } = props;

    const session = Math.random().toFixed(6);
    const onBlur = (e) => {
        if (e.relatedTarget && e.relatedTarget.dataset["session"] === session) {
            return true;
        }

        onChange(defaultValue, e.target.value);
    };
    const onKeyUp = (e) => {
        if (e.key === "Enter") {
            onChange(defaultValue, e.target.value);
            e.target.blur();
        }
    };

    // TODO: sometimes event has no time to fire because user hold the mouse key
    // as a fix to this problem we may consider to react to 'mousedown' event
    const onRemoveClicked = () => {
        onChange(defaultValue, null, true);
    };

    return (
        <div key={defaultValue} className="cell cell-header editable">
            <input {...props} key={defaultValue + session} onKeyUp={onKeyUp} onBlur={onBlur} onChange={undefined} />
            <div className="remove" tabIndex={0} data-session={session} onClick={onRemoveClicked}>&#x274C;</div>
        </div>
    );
}



// WEBPACK FOOTER //
// ./app/components/Gazetteers/Page.jsx